import React, { useState, useEffect, useMemo } from 'react';
import { styled } from 'styletron-react';
import { HeadingMedium, ParagraphSmall } from 'baseui/typography';
import { Spinner } from 'baseui/spinner';
import { toaster } from 'baseui/toast';
import { Shield, Users, ChevronRight, AlertTriangle, Plus, Check, Info } from 'lucide-react';
import { TableContent } from '../LayoutPage';
import * as samApi from '../../api/samApi';

const Container = styled('div', { backgroundColor: '#fff', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' });
const Header = styled('div', { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px' });
const Main = styled('div', { flex: 1, overflow: 'auto', padding: '0 24px 24px' });

const AlertBanner = styled('div', {
  display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '16px 20px',
  backgroundColor: '#FEF3C7', borderRadius: '12px', marginBottom: '20px', border: '1px solid #FDE68A',
});

const CapabilityNote = styled('div', {
  display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '16px 20px',
  backgroundColor: '#EFF6FF', borderRadius: '12px', marginBottom: '20px', border: '1px solid #BFDBFE',
});

const GroupCard = styled('div', {
  display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 20px',
  backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #E5E7EB',
  marginBottom: '8px', cursor: 'pointer', transition: 'all 0.15s ease',
  ':hover': { backgroundColor: '#F9FAFB', borderColor: '#D1D5DB' },
});

const MissingGroupCard = styled('div', {
  display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 20px',
  backgroundColor: '#FEF2F2', borderRadius: '12px', border: '1px dashed #FECACA',
  marginBottom: '8px',
});

const GroupIcon = styled('div', (props: { $exists: boolean }) => ({
  width: '40px', height: '40px', borderRadius: '10px',
  backgroundColor: props.$exists ? '#F3F4F6' : '#FEE2E2',
  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
}));

const GroupInfo = styled('div', { flex: 1, minWidth: 0 });
const GroupName = styled('div', { fontSize: '14px', fontWeight: 600, color: '#111827' });
const GroupDesc = styled('div', { fontSize: '12px', color: '#6B7280', marginTop: '2px' });

const Badge = styled('span', (props: { $color?: string }) => ({
  padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
  letterSpacing: '0.03em', fontFamily: 'monospace',
  backgroundColor: props.$color === 'red' ? '#FEE2E2' : '#EEF2FF',
  color: props.$color === 'red' ? '#991B1B' : '#4338CA',
  whiteSpace: 'nowrap',
}));

const CreateBtn = styled('button', {
  padding: '6px 14px', borderRadius: '8px', border: 'none',
  backgroundColor: 'var(--tenant-primary, #111827)', color: '#fff', fontSize: '12px', fontWeight: 500,
  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
  transition: 'all 0.15s ease', whiteSpace: 'nowrap',
  ':hover': { backgroundColor: 'color-mix(in srgb, var(--tenant-primary, #111827) 85%, black)' },
  ':disabled': { opacity: 0.5, cursor: 'not-allowed' },
});

const SectionLabel = styled('div', {
  fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.05em', color: '#9CA3AF', marginBottom: '12px', marginTop: '24px',
});

// All groups the system needs
const REQUIRED_GROUPS: Record<string, { label: string; description: string }> = {
  business_admin: { label: 'Business admin', description: 'Services, bookings, payments, events and team of their own business' },
  business_invitation: { label: 'Business Passes', description: 'Create and manage business guest passes' },
  business_residential_invitation: { label: 'Residential Passes', description: 'Create and manage residential guest passes' },
  business_invitation_all: { label: 'All Passes', description: 'Manage business and residential passes across all businesses' },
  business_payments: { label: 'Transactions', description: 'View and manage transactions & products' },
  business_services: { label: 'Services', description: 'Configure booking services' },
  business_bookings: { label: 'Bookings', description: 'View and manage bookings' },
  business_events: { label: 'Events', description: 'Create and manage events' },
  business_event_dashboard: { label: 'Event Dashboard (read-only)', description: 'View event dashboards and export CSV only — cannot create or edit events' },
  business_users: { label: 'Users', description: 'Manage application users' },
  business_estates: { label: 'Estates', description: 'Manage estates and properties' },
  business_property_manager: { label: 'Property Manager', description: 'Property administration' },
  business_cms: { label: 'CMS', description: 'Manage articles, ads, and home screen' },
  business_roles: { label: 'Roles', description: 'Manage roles and app users' },
  business_insights: { label: 'Insights', description: 'Access analytics and reports' },
  business_set_password: { label: 'Set Password', description: 'Can set passwords for users' },
  estate_group_admin: { label: 'Estate group admin', description: 'Passes, estates and businesses of their own estate groups' },
  estate_group_front_desk: { label: 'Estate group front desk', description: 'Passes of their own estate groups; view only elsewhere' },
  tenantSettings: { label: 'Tenant Settings', description: 'Configure tenant-level settings' },
  dashboard: { label: 'Dashboard', description: 'Dashboard-only read access' },
};

interface GroupInfo {
  name: string;
  label: string;
  description: string;
  exists: boolean;
  createdAt?: string;
}

export const PermissionGroups: React.FC = () => {
  const [existingGroups, setExistingGroups] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  // SAM-1311: a failed read (403 for xhinola_admin) says nothing about which groups exist.
  const [loadFailed, setLoadFailed] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [groupUsers, setGroupUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState<string | null>(null);

  useEffect(() => { loadGroups(); }, []);

  const loadGroups = async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const response = await samApi.listBusinessGroups({ quiet: true }) as any;
      const groupsData = response?.data?.groups || response?.groups || response?.data || [];
      const names = (Array.isArray(groupsData) ? groupsData : [])
        .map((g: any) => g.name || g.GroupName || '')
        .filter(Boolean);
      setExistingGroups(names);
    } catch (err: any) {
      setLoadFailed(true);
      // the notice on the page says it (SAM-1320, U41): no toast on top
    } finally {
      setLoading(false);
    }
  };

  const allGroups = useMemo<GroupInfo[]>(() => {
    const groups: GroupInfo[] = [];
    // Required groups first
    for (const [id, meta] of Object.entries(REQUIRED_GROUPS)) {
      groups.push({ name: id, label: meta.label, description: meta.description, exists: existingGroups.includes(id) });
    }
    // Extra groups from Cognito not in our required list
    for (const name of existingGroups) {
      if (!REQUIRED_GROUPS[name]) {
        groups.push({ name, label: name, description: 'Custom group', exists: true });
      }
    }
    return groups;
  }, [existingGroups]);

  const missingGroups = loadFailed ? [] : allGroups.filter((g) => !g.exists);
  const activeGroups = loadFailed ? [] : allGroups.filter((g) => g.exists);

  const handleCreateGroup = async (groupName: string) => {
    setCreatingGroup(groupName);
    try {
      const meta = REQUIRED_GROUPS[groupName];
      await samApi.createGroup(groupName, meta?.description || '');
      toaster.positive(`Group "${meta?.label || groupName}" created`, { autoHideDuration: 3000 });
      setExistingGroups((prev) => [...prev, groupName]);
    } catch (err: any) {
      toaster.negative(err?.message || 'Failed to create group', { autoHideDuration: 3000 });
    } finally {
      setCreatingGroup(null);
    }
  };

  const handleCreateAll = async () => {
    for (const group of missingGroups) {
      await handleCreateGroup(group.name);
    }
  };

  const toggleGroup = async (groupName: string) => {
    if (expandedGroup === groupName) {
      setExpandedGroup(null);
      setGroupUsers([]);
      return;
    }
    setExpandedGroup(groupName);
    setLoadingUsers(true);
    try {
      const response = await samApi.listGroupUsers(groupName) as any;
      const usersData = response?.data?.users || response?.users || response?.data || [];
      setGroupUsers(Array.isArray(usersData) ? usersData : []);
    } catch {
      setGroupUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  if (loading) {
    return (
      <TableContent>
        <Container>
          <Header><HeadingMedium margin={0}>Permissions</HeadingMedium></Header>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}><Spinner /></div>
        </Container>
      </TableContent>
    );
  }

  return (
    <TableContent>
      <Container>
        <Header>
          <div>
            <HeadingMedium margin={0}>Permissions</HeadingMedium>
            <ParagraphSmall marginTop="4px" marginBottom={0} color="#6B7280">
              Cognito groups that control access to admin features.
            </ParagraphSmall>
          </div>
        </Header>

        <Main>
          {/* SAM-544 (N03): these are admin-panel feature groups — they decide
              which pages/menu items show up in THIS app. They are a separate
              system from the tenant/business capability matrix the SAM guest
              app's backend enforces (SAM-538, `capability_matrix.py`), which
              only recognizes the literal Cognito groups `admin` (tenant-wide)
              and `business_admin` (scoped to one business) for
              manage_services / manage_bookings / manage_payments /
              void_payment / refund_payment / manage_permissions. The name
              `business_admin` collides between the two systems — here it
              means "full access to this admin panel"; on the backend it
              means "business-scoped capability grant, not tenant-wide". A
              user only gets void/refund/service-write access on the guest
              app's API because of THAT group, not because they can see
              every page in this panel. `manage_merchant` is granted to no
              group (Xhinola configures merchants directly in Postgres — no
              endpoint exists), and `manage_permissions` is defined but not
              enforced by any endpoint yet. Neither is editable here. */}
          <CapabilityNote>
            <Info size={20} color="#2563EB" style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#1E3A8A' }}>
                These groups control this admin panel's pages, not the guest app's API
              </div>
              <div style={{ fontSize: '13px', color: '#1D4ED8', marginTop: 4, lineHeight: 1.5 }}>
                Voiding/refunding a payment and writing to the service catalog are gated server-side
                by the Cognito groups <code>admin</code> (tenant-wide) and <code>business_admin</code> (scoped to one
                business) — see SAM-538. Configuring merchant credentials (<code>manage_merchant</code>) is
                exclusive to Xhinola and has no API endpoint; assigning capabilities to a user
                (<code>manage_permissions</code>) is defined but not enforced by any endpoint yet. Neither can be
                managed from this screen.
              </div>
            </div>
          </CapabilityNote>

          {loadFailed && (
            <AlertBanner role="alert">
              <AlertTriangle size={20} color="#D97706" style={{ flexShrink: 0, marginTop: 2 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#92400E' }}>Could not load the permission groups</div>
                <div style={{ fontSize: '13px', color: '#A16207', marginTop: 4 }}>
                  This account is not allowed to read them, or the request failed. Nothing is reported as missing.
                </div>
              </div>
              <CreateBtn onClick={loadGroups}>Retry</CreateBtn>
            </AlertBanner>
          )}

          {/* Missing groups alert */}
          {missingGroups.length > 0 && (
            <>
              <AlertBanner>
                <AlertTriangle size={20} color="#D97706" style={{ flexShrink: 0, marginTop: 2 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#92400E' }}>
                    {missingGroups.length} required group{missingGroups.length > 1 ? 's' : ''} not created
                  </div>
                  <div style={{ fontSize: '13px', color: '#A16207', marginTop: 4 }}>
                    These groups are needed by the app but don't exist in Cognito yet. Features that depend on them won't work until created.
                  </div>
                </div>
                <CreateBtn onClick={handleCreateAll} disabled={creatingGroup !== null}>
                  <Plus size={14} />
                  Create all
                </CreateBtn>
              </AlertBanner>

              <SectionLabel>Missing Groups</SectionLabel>
              {missingGroups.map((group) => (
                <MissingGroupCard key={group.name}>
                  <GroupIcon $exists={false}>
                    <AlertTriangle size={18} color="#DC2626" />
                  </GroupIcon>
                  <GroupInfo>
                    <GroupName>{group.label}</GroupName>
                    <GroupDesc>{group.description}</GroupDesc>
                  </GroupInfo>
                  <Badge $color="red">{group.name}</Badge>
                  <CreateBtn
                    onClick={() => handleCreateGroup(group.name)}
                    disabled={creatingGroup === group.name}
                  >
                    {creatingGroup === group.name ? <Spinner $size={12} /> : <Plus size={14} />}
                    Create
                  </CreateBtn>
                </MissingGroupCard>
              ))}
            </>
          )}

          {/* Active groups */}
          {!loadFailed && <SectionLabel>{missingGroups.length > 0 ? 'Active Groups' : 'All Groups'}</SectionLabel>}
          {activeGroups.map((group) => (
            <div key={group.name}>
              <GroupCard onClick={() => toggleGroup(group.name)}>
                <GroupIcon $exists={true}>
                  <Shield size={18} color="#6B7280" />
                </GroupIcon>
                <GroupInfo>
                  <GroupName>
                    {group.label}
                    {group.name === 'business_admin' && (
                      <span style={{ fontSize: '10px', color: '#059669', marginLeft: 8, fontWeight: 500 }}>FULL ACCESS</span>
                    )}
                  </GroupName>
                  <GroupDesc>{group.description}</GroupDesc>
                </GroupInfo>
                <Badge>{group.name}</Badge>
                <ChevronRight
                  size={16} color="#9CA3AF"
                  style={{ transform: expandedGroup === group.name ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s ease' }}
                />
              </GroupCard>

              {expandedGroup === group.name && (
                <div style={{ padding: '8px 20px 16px 76px', borderBottom: '1px solid #F3F4F6', marginBottom: '8px' }}>
                  {loadingUsers ? (
                    <Spinner $size={20} />
                  ) : groupUsers.length === 0 ? (
                    <ParagraphSmall margin={0} color="#9CA3AF" style={{ fontStyle: 'italic' }}>
                      No users in this group
                    </ParagraphSmall>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <ParagraphSmall margin="0 0 8px" color="#6B7280" style={{ fontSize: '12px' }}>
                        {groupUsers.length} user{groupUsers.length !== 1 ? 's' : ''}
                      </ParagraphSmall>
                      {groupUsers.map((u: any, i: number) => {
                        const email = u.email || u.Attributes?.find((a: any) => a.Name === 'email')?.Value || '';
                        const name = u.name || u.Attributes?.find((a: any) => a.Name === 'name')?.Value || '';
                        const familyName = u.family_name || u.Attributes?.find((a: any) => a.Name === 'family_name')?.Value || '';
                        const displayName = [name, familyName].filter(Boolean).join(' ') || u.username || u.Username;
                        return (
                          <div key={u.username || u.Username || i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
                            <Users size={14} color="#9CA3AF" />
                            <span style={{ fontSize: '13px', fontWeight: 500, color: '#111827' }}>{displayName}</span>
                            {email && <span style={{ fontSize: '12px', color: '#9CA3AF' }}>({email})</span>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* All OK indicator */}
          {!loadFailed && missingGroups.length === 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '16px 0', color: '#059669' }}>
              <Check size={16} />
              <span style={{ fontSize: '13px', fontWeight: 500 }}>All required permission groups are created</span>
            </div>
          )}
        </Main>
      </Container>
    </TableContent>
  );
};
